// src/pages/PublicCatalog/components/ProductPage.js
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Container,
  Grid,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  TextField,
  Divider,
  InputAdornment
} from '@material-ui/core';
import {
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Share as ShareIcon,
  FileCopy as FileCopyIcon
} from '@material-ui/icons';
import { getBackendUrl } from "../../../config";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  breadcrumbs: {
    marginBottom: theme.spacing(2),
  },
  mainContent: {
    marginTop: theme.spacing(2),
  },
  imageContainer: {
    position: 'relative',
    height: 400,
    backgroundColor: theme.palette.grey[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.down('sm')]: {
      height: 300,
    },
  },
  productImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  navigationArrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
    },
    zIndex: 2,
  },
  leftArrow: {
    left: theme.spacing(1),
  },
  rightArrow: {
    right: theme.spacing(1),
  },
  thumbnailsContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    overflowX: 'auto',
    padding: theme.spacing(1),
    '&::-webkit-scrollbar': {
      height: 6,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.grey[200],
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[400],
      borderRadius: 3,
    },
  },
  thumbnail: {
    width: 60,
    height: 60,
    objectFit: 'cover',
    cursor: 'pointer',
    border: `2px solid transparent`,
    borderRadius: theme.shape.borderRadius,
    '&.active': {
      borderColor: theme.palette.primary.main,
    },
  },
  productInfo: {
    padding: theme.spacing(2),
  },
  productName: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  price: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    fontSize: '1.8rem',
    marginBottom: theme.spacing(2),
  },
  description: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    whiteSpace: 'pre-line',
  },
  attributesTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  attributes: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  quantityControl: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
  },
  quantity: {
    width: 60,
    textAlign: 'center',
  },
  addToCartButton: {
    marginTop: theme.spacing(2),
  },
  categoryChip: {
    marginBottom: theme.spacing(2),
  },
  shareLink: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  backToProducts: {
    marginTop: theme.spacing(3),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

const ProductPage = ({ product, onAddToCart, onBack, shareableLink }) => {
  const classes = useStyles();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);

  if (!product) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  const handleAddToCart = () => {
    onAddToCart(product.id, quantity);
    // Use the toast notification
    toast.success(`${quantity} x ${product.name} agregado al carrito`);
  };
  const copyShareableLink = () => {
    navigator.clipboard.writeText(shareableLink);
    toast.success("Enlace copiado al portapapeles");
  };

  return (
    <div className={classes.root}>
      <Container>
        <Breadcrumbs className={classes.breadcrumbs}>
          <Link color="inherit" href="javascript:void(0)" onClick={onBack} display="flex" alignItems="center">
            <HomeIcon fontSize="small" style={{ marginRight: 4 }} />
            Catálogo
          </Link>
          {product.category && (
            <Link color="inherit" href={`?categoryId=${product.category.id}`}>
              {product.category.name}
            </Link>
          )}
          <Typography color="textPrimary">{product.name}</Typography>
        </Breadcrumbs>

        <Box className={classes.backToProducts} onClick={onBack}>
          <ArrowBackIcon fontSize="small" />
          <Typography variant="button">Volver al catálogo</Typography>
        </Box>

        <Grid container spacing={3} className={classes.mainContent}>
          <Grid item xs={12} md={6}>
            {product.images && product.images.length > 0 ? (
              <>
                <div className={classes.imageContainer}>
                  <img
                    src={`${getBackendUrl()}public/products/${product.images[currentImageIndex].imageUrl}`}
                    alt={product.name}
                    className={classes.productImage}
                  />
                  {product.images.length > 1 && (
                    <>
                      <IconButton
                        className={`${classes.navigationArrow} ${classes.leftArrow}`}
                        size="small"
                        onClick={() => setCurrentImageIndex(prev => Math.max(0, prev - 1))}
                        disabled={currentImageIndex === 0}
                      >
                        <NavigateBeforeIcon />
                      </IconButton>
                      <IconButton
                        className={`${classes.navigationArrow} ${classes.rightArrow}`}
                        size="small"
                        onClick={() => setCurrentImageIndex(prev => Math.min(product.images.length - 1, prev + 1))}
                        disabled={currentImageIndex === product.images.length - 1}
                      >
                        <NavigateNextIcon />
                      </IconButton>
                    </>
                  )}
                </div>
                {product.images.length > 1 && (
                  <div className={classes.thumbnailsContainer}>
                    {product.images.map((image, index) => (
                      <img
                        key={index}
                        src={`${getBackendUrl()}public/products/${image.imageUrl}`}
                        alt={`${product.name} ${index + 1}`}
                        className={`${classes.thumbnail} ${currentImageIndex === index ? 'active' : ''}`}
                        onClick={() => setCurrentImageIndex(index)}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className={classes.imageContainer}>
                <Typography variant="body2">Sin imagen</Typography>
              </div>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper className={classes.productInfo} elevation={0} variant="outlined">
              {product.category && (
                <Chip
                  label={product.category.name}
                  color="primary"
                  variant="outlined"
                  size="small"
                  className={classes.categoryChip}
                />
              )}

              <Typography variant="h4" className={classes.productName}>
                {product.name}
              </Typography>

              <Typography className={classes.price}>
                {new Intl.NumberFormat('es-ES', {
                  style: 'currency',
                  currency: product.currency || 'USD'
                }).format(product.price)}
              </Typography>

              <Divider />

              {product.description && (
                <Typography variant="body1" className={classes.description}>
                  {product.description}
                </Typography>
              )}

              {product.attributes && product.attributes.length > 0 && (
                <>
                  <Typography variant="subtitle1" className={classes.attributesTitle}>
                    Características
                  </Typography>
                  <div className={classes.attributes}>
                    {product.attributes.map((attr, index) => (
                      <Chip
                        key={index}
                        label={`${attr.name}: ${attr.value}`}
                        size="small"
                      />
                    ))}
                  </div>
                </>
              )}

              <div className={classes.quantityControl}>
                <IconButton
                  size="small"
                  onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                  disabled={quantity <= 1}
                >
                  <RemoveIcon />
                </IconButton>
                <TextField
                  className={classes.quantity}
                  value={quantity}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value > 0) {
                      setQuantity(value);
                    }
                  }}
                  inputProps={{ min: 1, style: { textAlign: 'center' } }}
                  variant="outlined"
                  size="small"
                />
                <IconButton
                  size="small"
                  onClick={() => setQuantity(prev => prev + 1)}
                >
                  <AddIcon />
                </IconButton>
              </div>

              <Button
                variant="contained"
                color="primary"
                fullWidth
                className={classes.addToCartButton}
                onClick={handleAddToCart}
                size="large"
              >
                Agregar al carrito
              </Button>

              {shareableLink && (
                <div className={classes.shareLink}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <ShareIcon fontSize="small" style={{ marginRight: 8 }} />
                    <Typography variant="subtitle2">Compartir producto</Typography>
                  </Box>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={shareableLink}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            onClick={copyShareableLink} 
                            size="small"
                          >
                            <FileCopyIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default ProductPage;